import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { customValidator } from 'src/app/shared/validators/fields-validators';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { selectCanDo } from 'src/app/core/store/permissions/permissions.selectors';
import { Subscription } from 'rxjs';
import { HasPermissionContext } from 'src/app/shared/has-permission.directive';

@Component({
  selector: 'app-edit-general-rules',
  templateUrl: './edit-general-rules.component.html',
  styleUrls: ['./edit-general-rules.component.scss']
})
export class EditGeneralRulesComponent implements OnInit {

  form!: FormGroup;
  selectedProducts: IProduct[] = [];
  canSubmit = false;
  isEdit = false;
  private subscriptions: Subscription[] = [];
  constructor(
    public dialogRef: MatDialogRef<EditGeneralRulesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private translate: TranslateService,
    private store: Store<AppState>,
  ) { }

  ngOnInit(): void {
    this.selectedProducts = this.data.selectedProducts;
    this.isEdit = !!this.data?.isEdit;
    this.form = this.fb.group({
      general_discount: new FormControl(this.data.generalRules?.general_discount ?? '', Validators.required),
      increment: new FormControl(this.data.generalRules?.increment ?? 1, [Validators.required]),
      max_qte: new FormControl(this.data.generalRules?.max_qte ?? null),
      min_qte: new FormControl(this.data.generalRules?.min_qte ?? 1, [Validators.required]),
      can_be_sold_out_of_stock: new FormControl(this.data.generalRules?.can_be_sold_out_of_stock ?? false, [Validators.required]),
      show_stock: new FormControl(this.data.generalRules?.show_stock ?? true, [Validators.required]),
    }, { validators: customValidator(this.translate) });

    this.form.get('show_stock')?.valueChanges.subscribe((value: boolean) => {
      if (value === false) this.form.get('can_be_sold_out_of_stock')?.setValue(true);
    })

    this.form.get('can_be_sold_out_of_stock')?.valueChanges.subscribe((value: boolean) => {
      if (value === false) this.form.get('show_stock')?.setValue(true);
    });

    const action = this.isEdit ? 'edit' : 'create';
    const sub = this.store.select(selectCanDo('catalog', action)).subscribe((canSubmit) => {
      this.canSubmit = canSubmit;
      canSubmit ? this.form.enable({ emitEvent: false }) : this.form.disable({ emitEvent: false });
    });
    this.subscriptions.push(sub);
  }

  onGeneralDiscountChange(event: any){
    if (!this.canSubmit) return;
    const input = event.target, key = event.data;
    if ([',', '.'].includes(key)) {
      return;
    }
    const value = input.value < 0 ? 0 : input.value > 100 ? 100 : +input.value;
    input.value = value

    this.form.get('general_discount')?.setValue(value);
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            discount: value,
          }
        })
      }
      return {
        ...product,
        discount: value,
      }
    })
  }
  
  onGeneralIncrementChange(event: any){
    if (!this.canSubmit) return;
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            increment: event.target.value ? +event.target.value : undefined
          }
        })
      }
      return {
        ...product,
        increment: event.target.value ? +event.target.value : undefined,
      }
    })
  
  }
  onGeneralMinChange(event: any){
    if (!this.canSubmit) return;
    const value = event.target.value;
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            min_qte: value ? +value : undefined,
          }
        })
      }
      return {
        ...product,
        min_qte: value ? +value : undefined,
      }
    })
  }
  onGeneralMaxChange(event: any){
    if (!this.canSubmit) return;
    const value = event.target.value;
    this.selectedProducts = this.selectedProducts.map((product: IProduct, index) => {
      if(product.variants && product.variants.length > 0 ){
        this.selectedProducts[index].variants = this.selectedProducts[index].variants?.map(variant => {
          return {
            ...variant,
            max_qte: value ? +value : undefined
          }
        })
      }
      return {
        ...product,
        max_qte: value ? +value : undefined,
      }
    })
  }

  sanitize(event: any) {
    if (!this.canSubmit) return;
    event.target.value = event.target.value.replace(/[^\d]/g, '');
  }

  save(): boolean | void{
    if (!this.canSubmit) return;
    if(this.form.invalid){
      this.form.markAllAsTouched()
      return false
    }
    this.dialogRef.close({
      generalRules: this.form.value,
      selectedProducts: this.selectedProducts
    });
  }
  closed(){
    this.dialogRef.close();
  }

  submitPermission(): HasPermissionContext {
    return { module: 'catalog', action: this.isEdit ? 'edit' : 'create' };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

}
